import { Button } from "@/components/ui/button";
import { Download, FileText, Package, Sparkles } from "lucide-react";

export default function ReportTab({ project, onDownload }) {
  const done = project.status === "complete";
  return (
    <div className="space-y-8">
      <div className="border border-border rounded-sm p-8 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-accent" />
          <span className="label-mono">// FINAL REPORT</span>
        </div>
        <h2 className="font-display font-bold text-3xl mb-2">{project.name}</h2>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Automated ML report combining dataset profile, preprocessing decisions, model leaderboard
          and best-model evaluation. Export as HTML or download the deployable pipeline.
        </p>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="TARGET" value={project.target_column || "—"} />
          <Stat label="PROBLEM" value={project.problem_type || "—"} />
          <Stat label="BEST MODEL" value={project.best_model_name || "—"} accent />
          <Stat label="QUALITY" value={project.quality_score || "—"} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ArtifactCard
          icon={Package}
          title="Deployable Pipeline"
          desc="Joblib file containing full preprocessing + estimator. Load with joblib.load()."
          filename="pipeline.joblib"
          onClick={() => onDownload("pipeline")}
          testId="report-download-pipeline"
          disabled={!done}
        />
        <ArtifactCard
          icon={Package}
          title="Best Model"
          desc="Same as pipeline but named for the winning estimator."
          filename={`${project.best_model_name || "model"}.joblib`}
          onClick={() => onDownload("model")}
          testId="report-download-model"
          disabled={!done}
        />
        <ArtifactCard
          icon={FileText}
          title="PDF Report"
          desc="Executive-ready PDF with dataset, preprocessing, leaderboard, tuning, SHAP."
          filename="report.pdf"
          onClick={() => onDownload("pdf")}
          testId="report-download-pdf"
          disabled={!done}
        />
        <ArtifactCard
          icon={FileText}
          title="HTML Report"
          desc="Standalone HTML with the same content, easy to publish or share."
          filename="report.html"
          onClick={() => onDownload("report")}
          testId="report-download-report"
          disabled={!done}
        />
      </div>

      {!done && (
        <div className="border border-border rounded-sm p-6 text-sm text-muted-foreground">
          Artifacts appear here once the pipeline reaches <span className="font-mono text-accent">complete</span>.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className={`num font-mono text-base mt-1 truncate ${accent ? "text-accent" : ""}`}>{value}</div>
    </div>
  );
}

function ArtifactCard({ icon: Icon, title, desc, filename, onClick, testId, disabled }) {
  return (
    <div className={`border border-border rounded-sm p-5 bg-card flex flex-col ${disabled ? "opacity-50" : ""}`}>
      <Icon className="w-5 h-5 text-accent mb-3" strokeWidth={1.5} />
      <div className="font-display font-semibold">{title}</div>
      <p className="text-xs text-muted-foreground mt-1 flex-1">{desc}</p>
      <div className="font-mono text-xs text-muted-foreground mt-3">{filename}</div>
      <Button
        onClick={onClick}
        disabled={disabled}
        className="mt-3 bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm"
        data-testid={testId}
      >
        <Download className="w-3.5 h-3.5 mr-2" /> Download
      </Button>
    </div>
  );
}
